import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { API_URL } from '../config';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './component/Header';
import PublicFooter from '../components/layout/PublicFooter';
import { StepIndicator } from '../components/ui';
import Swal from 'sweetalert2';
import StateMunicipalityDropdown from './component/StateMunicipalityDropdown';
import CustomFields from './component/CustomFields';

const SignupWizard = () => {
  const { onboardingId } = useParams();
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedStateLabel, setSelectedStateLabel] = useState(null);
  const [formPlateNo, setFormPlateNo] = useState('');
  const [formLastSixDigitsOfVIN, setFormLastSixDigitsOfVIN] = useState('');
  const [formPlateType, setFormPlateType] = useState(null);
  const [formExpiration, setFormExpiration] = useState(null);
  const [plateTypeOptions, setPlateTypeOptions] = useState([]);
  const [plateList, setPlateList] = useState([]);
  const [errors, setErrors] = useState({});
  const [currentStep] = useState(2);
  const [stepsName, setStepsName] = useState({});
  const [customFieldsFormValues, setCustomFieldsFormValues] = useState({});
  const [pages, setPage] = useState(null);
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [nextStepId, setNextStepId] = useState(null);

  const customDateFormat = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${month}/${day}/${year}`;
  };

  const vinText = () => {
    switch (selectedStateLabel) {
      case 'Idaho': return 'Last 6 digits of VIN';
      case 'Michigan': return 'Last 4 digits of VIN';
      case 'Texas': return 'Last 4 digits of VIN';
      default: return '';
    }
  };

  const customYMDateFormat = (dateString) => {
    const [year, month] = dateString.split('-');
    const day = new Date(year, month, 0, 0).getDate();
    return `${month}/${day}/${year}`;
  };

  const dateBeforeDays = (days) => {
    return new Date(new Date().setDate(new Date().getDate() - days));
  };

  const dateBeforeDaysCustomDateFormat = (days) => {
    let date = dateBeforeDays(days);
    let year = date.getFullYear();
    let month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCustomFields = useCallback((stateId) => {
    if (stateId !== undefined && stateId !== null) {
      fetch(API_URL + `/custom-step-sections/${stateId}`)
        .then((response) => response.json())
        .then((data) => setPage(data.pages))
        .catch((error) => console.error('Error fetching custom step page data:', error));
    }
  }, []);

  const fetchPlateTypes = useCallback((stateId) => {
    if (stateId !== undefined && stateId !== null) {
      fetch(API_URL + `/plate-types?state_id=${stateId}`)
        .then((response) => response.json())
        .then((data) => {
          if (Array.isArray(data)) {
            const options = data.map((plateType) => ({ value: plateType.id, label: plateType.name }));
            setPlateTypeOptions(options);
            if (options[0]) setFormPlateType(options[0]);
            fetchCustomFields(stateId);
          }
        })
        .catch((error) => {});
    }
  }, [fetchCustomFields]);

  useEffect(() => {
    fetch(API_URL + '/municipalities')
      .then((response) => response.json())
      .then((data) => setStateOptions(data))
      .catch((error) => console.error('Error fetching municipalities:', error));
  }, []);

  useEffect(() => {
    fetch(API_URL + `/onboarding/${onboardingId}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          navigate('/');
        } else {
          const onboardingStateId = data.onboarding.onboarding_detail.municipality_id;
          setSelectedState(onboardingStateId);
          setFormPlateNo(data.onboarding.onboarding_detail.plate_number);
          setStepsName(data.steps.stepsName);
          setNextStepId(data.steps.id);
          fetchPlateTypes(onboardingStateId);
        }
      })
      .catch((error) => console.error('Error fetching onboarding data:', error));
  }, [onboardingId, stateOptions, navigate, fetchPlateTypes]);

  const isFormReady = () => {
    return plateList.length > 0 || ((selectedStateLabel == 'Idaho' || selectedStateLabel == 'Michigan' || selectedStateLabel == 'Texas') && formPlateNo && selectedState && formExpiration && formLastSixDigitsOfVIN) || (selectedStateLabel != 'Idaho' && selectedStateLabel != 'Michigan' && selectedStateLabel != 'Texas' && formPlateNo && selectedState && formExpiration);
  };

  const validatePlateNumber = (plateNumber) => /^[A-Za-z0-9]*$/.test(plateNumber);

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setFormPlateType(null);
    setPlateTypeOptions([]);
    setFormExpiration(null);
    setPage(null);
    if (selectedOption) {
      if (typeof selectedOption == 'object' && selectedOption.value) {
        let _stateLable = stateOptions.find((state) => state.children.some((municipality) => municipality.value === selectedOption.value));
        setSelectedStateLabel(_stateLable.label);
      } else {
        let _stateLable = stateOptions.find((state) => state.children.some((municipality) => municipality.value === selectedOption));
        setSelectedStateLabel(_stateLable.label);
      }
      fetchPlateTypes(selectedOption.value);
    }
  };

  const handleAddPlate = (e) => {
    e.preventDefault();
    let plateErrors = {};
    if (!selectedState) plateErrors.state = 'Please select a county.';
    if (!formPlateNo) plateErrors.plateNo = 'Please enter the plate number.';
    else if (!validatePlateNumber(formPlateNo)) plateErrors.plateNo = 'Please enter a valid plate number.';
    if (!formLastSixDigitsOfVIN && (selectedStateLabel == 'Idaho' || selectedStateLabel == 'Michigan' || selectedStateLabel == 'Texas')) {
      plateErrors.lastSixDigitsOfVIN = 'Please enter the ' + vinText();
    }
    if (!formExpiration) plateErrors.expiration = 'Please select the expiration date.';
    const isDuplicatePlateNo = plateList.some((plate) => plate.plateNo.toLowerCase() === formPlateNo.toLowerCase());
    if (isDuplicatePlateNo) plateErrors.plateNo = 'This plate number already exists.';

    const customFieldData = {};
    if (pages !== undefined && pages !== null) {
      pages.forEach((page) => {
        page.fields.forEach((field) => {
          customFieldData[field.custom_field.id] = customFieldsFormValues[field.custom_field.id];
          if (field.custom_field.is_required && !customFieldsFormValues[field.custom_field.id]) {
            plateErrors[field.custom_field.id] = `${field.custom_field.field_name} is required.`;
          }
        });
      });
    }

    if (Object.keys(plateErrors).length > 0) {
      setErrors({ ...errors, ...plateErrors });
      Swal.fire({ icon: 'error', title: 'Please fix the following issues:', html: Object.values(plateErrors).join('<br>'), showCloseButton: true });
      return;
    }

    const newPlate = {
      plateNo: formPlateNo,
      lastSixDigitsOfVIN: formLastSixDigitsOfVIN,
      selectedState,
      expiration: isMobile ? customYMDateFormat(formExpiration) : formExpiration.toLocaleDateString('en-US'),
      customFields: customFieldData,
    };
    setPlateList([...plateList, newPlate]);
    setFormPlateNo('');
    setFormLastSixDigitsOfVIN('');
    setFormPlateType(null);
    setFormExpiration(null);
    setErrors({});
  };

  const handleFormSubmit = async () => {
    let newPlate = {};
    if (formPlateNo && selectedState && formExpiration) {
      const customFieldData = {};
      if (pages !== undefined && pages !== null) {
        pages.forEach((page) => {
          page.fields.forEach((field) => {
            customFieldData[field.custom_field.id] = customFieldsFormValues[field.custom_field.id];
          });
        });
      }
      newPlate = {
        plateNo: formPlateNo,
        lastSixDigitsOfVIN: formLastSixDigitsOfVIN,
        selectedState,
        expiration: isMobile ? customYMDateFormat(formExpiration) : formExpiration.toLocaleDateString('en-US'),
        customFields: customFieldData,
      };
    }

    const allPlates = [...plateList, newPlate].filter((plate) => Object.keys(plate).length > 0);
    try {
      const response = await fetch(API_URL + '/onboarding/store-plates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plates: allPlates.map((plate) => ({
            plate_number: plate.plateNo,
            last_six_digits_of_vin: plate.lastSixDigitsOfVIN,
            expiration_date: plate.expiration,
            state_id: typeof plate.selectedState === 'object' ? plate.selectedState.value : plate.selectedState,
            customFields: plate.customFields,
          })),
          onboarding_id: onboardingId,
        }),
      });
      const data = await response.json();
      if (data.error) {
        setErrors({ serverError: data.error });
        Swal.fire({ icon: 'error', title: 'Submission failed. Error:', html: data.error, showCloseButton: true });
      } else {
        if (nextStepId) navigate(`/plate-information/${onboardingId}/${nextStepId}`);
        else navigate(`/billing-information/${onboardingId}`);
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Error submitting the form. Please try again later.' });
    }
  };

  const handleDeletePlate = (index) => {
    const updatedPlateList = [...plateList];
    updatedPlateList.splice(index, 1);
    setPlateList(updatedPlateList);
  };

  const inputClasses = "tw-w-full tw-rounded-lg tw-border tw-border-gray-400 tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100";

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
      <Header />

      {/* Hero Banner */}
      <div className="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-800 tw-py-10 md:tw-py-14">
        <div className="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8">
          <h1 className="tw-text-4xl tw-text-white tw-m-0">Plate Information</h1>
          <p className="tw-text-lg tw-text-primary-200 tw-mt-2 tw-mb-0">Add your vehicle details to get started with automatic renewal.</p>
        </div>
      </div>

      <div className="tw-max-w-3xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-8">
        {/* Step Indicator */}
        {stepsName && Object.keys(stepsName).length > 0 && (
          <StepIndicator steps={Object.values(stepsName)} currentStep={currentStep - 1} />
        )}

        {/* Form Card */}
        <div className="tw-bg-white tw-rounded-2xl tw-shadow-lg tw-border tw-border-gray-100 tw-p-6 md:tw-p-8">
          <h2 className="tw-text-2xl tw-text-gray-900 tw-m-0 tw-mb-6">Vehicle Details</h2>

          <form className="tw-space-y-7">
            {/* County */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <label className="tw-text-sm tw-font-medium tw-text-gray-700">County</label>
              <StateMunicipalityDropdown
                data={stateOptions}
                selectedMunicipality={selectedState}
                setSelectedMunicipality={handleStateChange}
              />
              {errors.state && <p className="tw-text-xs tw-text-error tw-m-0">{errors.state}</p>}
            </div>

            {/* Plate Number */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <label htmlFor="plateNoInput" className="tw-text-sm tw-font-medium tw-text-gray-700">License Plate Number</label>
              <input
                type="text"
                className={inputClasses}
                id="plateNoInput"
                placeholder="Enter your plate number"
                value={formPlateNo}
                onChange={(e) => setFormPlateNo(e.target.value ? e.target.value.replace(/[^0-9a-zA-Z]+/gi, '') : '')}
              />
              {errors.plateNo && <p className="tw-text-xs tw-text-error tw-m-0">{errors.plateNo}</p>}
            </div>

            {/* VIN (conditional) */}
            {(selectedStateLabel == 'Idaho' || selectedStateLabel == 'Michigan' || selectedStateLabel == 'Texas') && (
              <div className="tw-flex tw-flex-col tw-gap-1.5">
                <label htmlFor="lastSixDigitsOfVIN" className="tw-text-sm tw-font-medium tw-text-gray-700">{vinText()}</label>
                <input
                  type="text"
                  className={inputClasses}
                  id="lastSixDigitsOfVIN"
                  placeholder={vinText()}
                  value={formLastSixDigitsOfVIN}
                  maxLength={6}
                  onChange={(e) => setFormLastSixDigitsOfVIN(e.target.value)}
                />
                {errors.lastSixDigitsOfVIN && <p className="tw-text-xs tw-text-error tw-m-0">{errors.lastSixDigitsOfVIN}</p>}
              </div>
            )}

            {/* Expiration */}
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <label htmlFor="expirationInput" className="tw-text-sm tw-font-medium tw-text-gray-700">Expiration</label>
              {isMobile ? (
                <input
                  id="expirationInput"
                  className={inputClasses}
                  type="month"
                  value={formExpiration}
                  onChange={(e) => setFormExpiration(e.target.value)}
                  placeholder="Select expiration date"
                  min={dateBeforeDaysCustomDateFormat(45)}
                />
              ) : (
                <DatePicker
                  id="expirationInput"
                  className={inputClasses}
                  selected={formExpiration}
                  onKeyDown={(e) => e.preventDefault()}
                  onChange={(date) => setFormExpiration(new Date(date.getFullYear(), date.getMonth() + 1, 0, 0))}
                  showMonthYearPicker
                  dateFormat="MMM yyyy"
                  placeholderText="Select expiration date"
                  minDate={dateBeforeDays(45)}
                />
              )}
              {errors.expiration && <p className="tw-text-xs tw-text-error tw-m-0">{errors.expiration}</p>}
            </div>

            {/* Custom Fields */}
            <CustomFields setCustomFieldsFormValues={setCustomFieldsFormValues} customFieldsFormValues={customFieldsFormValues} pages={pages} errors={errors} setErrors={setErrors} />

            {/* Plate List (shown above buttons when plates exist) */}
            {plateList.length > 0 && (
              <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden">
                <div className="tw-bg-gray-50 tw-px-4 tw-py-2.5 tw-border-b tw-border-gray-200">
                  <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-m-0">{plateList.length} plate{plateList.length > 1 ? 's' : ''} added</h3>
                </div>
                <div className="tw-divide-y tw-divide-gray-100">
                  {plateList.map((plate, index) => (
                    <div key={index} className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
                      <div className="tw-flex tw-items-center tw-gap-4">
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-blue-100 tw-text-blue-600 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                          <svg className="tw-w-5 tw-h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm-3.375 6.375h4.5" /></svg>
                        </div>
                        <div>
                          <div className="tw-font-semibold tw-text-gray-900 tw-text-sm">{plate.plateNo}</div>
                          <div className="tw-text-xs tw-text-gray-500">Expires {plate.expiration}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeletePlate(index)}
                        className="tw-p-2 tw-rounded-lg hover:tw-bg-red-50 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-gray-400 hover:tw-text-red-500"
                        title="Remove plate"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Buttons */}
            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-pt-2">
              <button
                type="button"
                onClick={handleAddPlate}
                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-white tw-text-primary-600 tw-border tw-border-primary-200 hover:tw-bg-primary-50 tw-px-5 tw-py-3 tw-rounded-lg tw-text-sm tw-font-semibold tw-cursor-pointer tw-transition-all"
              >
                <FontAwesomeIcon icon={faPlus} /> {plateList.length > 0 ? 'Add Another Plate' : 'Add & Enter Another'}
              </button>
              <button
                type="button"
                onClick={handleFormSubmit}
                disabled={!isFormReady()}
                className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-accent-500 hover:tw-bg-accent-600 tw-text-white tw-px-8 tw-py-3 tw-rounded-lg tw-text-sm tw-font-semibold tw-border-0 tw-cursor-pointer tw-transition-all tw-shadow-sm hover:tw-shadow-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex-1 sm:tw-flex-none"
              >
                Continue to {nextStepId ? 'Next Step' : 'Billing'} <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </form>
        </div>

        {/* Security reassurance */}
        <div className="tw-mt-6 tw-flex tw-items-center tw-justify-center tw-gap-4 tw-text-xs tw-text-gray-400">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
            <span>256-bit SSL encryption</span>
          </div>
          <span className="tw-text-gray-300">·</span>
          <span>Your data is never shared</span>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};

export default SignupWizard;
